import { useId, useState } from 'react'
import type { FC, ReactNode } from 'react'
import { Box, Collapse } from '@mui/material'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'

interface CollapsibleSectionProps {
  /** Group heading, rendered uppercase (e.g. "Clinical"). */
  label: string
  /** Item count shown after the label in the mono stack. */
  count?: number
  defaultOpen?: boolean
  /** Controlled mode: pass `open` (+ `onToggle`) when the caller owns the
   * state, e.g. groups that auto-expand for a deep-linked case. */
  open?: boolean
  onToggle?: (open: boolean) => void
  children: ReactNode
}

/**
 * Collapsible group with the chevron + uppercase-label + count header used for
 * case categories, steerages, and prior auths. Uncontrolled by default
 * (`defaultOpen`), controllable via `open`/`onToggle`; content unmounts while
 * collapsed.
 */
export const CollapsibleSection: FC<CollapsibleSectionProps> = ({
  label,
  count,
  defaultOpen = true,
  open: controlledOpen,
  onToggle,
  children,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const open = controlledOpen ?? uncontrolledOpen
  const contentId = useId()

  return (
    <Box>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => {
          if (onToggle) {
            onToggle(!open)
          } else {
            setUncontrolledOpen(previous => !previous)
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 0,
          py: 0.25,
          mb: 1,
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        <ChevronRightRoundedIcon
          aria-hidden
          sx={theme => ({
            fontSize: '2rem',
            color: 'text.secondary',
            transform: open ? 'rotate(90deg)' : 'none',
            transition: theme.transitions.create('transform', {
              duration: theme.transitions.duration.shortest,
            }),
          })}
        />
        <Box
          component="span"
          sx={{
            fontSize: '1.2rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'text.secondary',
          }}
        >
          {label}
        </Box>
        {count !== undefined && (
          <Box
            component="span"
            sx={theme => ({
              fontFamily: theme.typography.fontFamilyMono,
              fontSize: '1.2rem',
              color: 'text.secondary',
            })}
          >
            {count}
          </Box>
        )}
      </Box>
      <Collapse in={open} unmountOnExit id={contentId}>
        {children}
      </Collapse>
    </Box>
  )
}
